import {
  isSubscriptionIncomplete,
  isSubscriptionIncompleteExpired,
} from "@/react/helpers/AccountSubscriptionHelpers";
import { isPromoCodeEnabled } from "@/react/helpers/communityHelpers";
import type { PlanTiers } from "@/react/helpers/planHelpers";
import {
  isAnnual,
  isPlanBasic,
  isPlanBusiness,
  isPlanEnterprise,
  isPlanPlus,
  isPlanProfessional,
  isUpgrading,
} from "@/react/helpers/planHelpers";
import { usePunditUserContext } from "@circle-react/contexts";
import { useIsAdminApiSubscriptionActive } from "@circle-react/hooks/useIsAdminApiSubscriptionActive";
import { useIsHeadlessApiSubscriptionActive } from "@circle-react/hooks/useIsHeadlessApiSubscriptionActive";
import { useIsEmailHubStatusEnabled } from "./useIsEmailHubStatus";

export const usePlanStatus = () => {
  const { currentCommunity, currentCommunitySettings } = usePunditUserContext();
  const {
    is_on_trial: isOnTrial,
    default_currency: defaultCurrency = null,
    is_expired_trial: isExpiredTrial = false,
    can_choose_currency: canChooseCurrency = false,
    current_plan_tier: currentPlanTier,
    current_plan_interval: currentPlanInterval,
    has_addons: hasAddons = false,
    has_active_offer: hasActiveOffer = false,
    current_plan: currentPlan,
    subscription: subscription,
  } = currentCommunity ?? {};

  const {
    current_period_end: currentPeriodEnd,
    current_period_start: currentPeriodStart,
  } = subscription ?? {};

  const isEmailHubStatusEnabled = useIsEmailHubStatusEnabled();
  const isAdminApiActive = useIsAdminApiSubscriptionActive();
  const isHeadlessApiActive = useIsHeadlessApiSubscriptionActive();
  const isCommunityOnTrial = Boolean(isOnTrial);
  const isCommunityOnPlus = isPlanPlus(currentPlanTier);
  const isCommunityOnEnterprise = isPlanEnterprise(currentPlanTier);
  const isCommunityOnProfessional = isPlanProfessional(currentPlanTier);
  const isCommunityOnBasic = isPlanBasic(currentPlanTier);
  const isCommunityOnBusiness = isPlanBusiness(currentPlanTier);
  const isPromoCodeEnabledForCommunity = isPromoCodeEnabled(currentCommunity);

  const isCommunitySubscriptionIncompleteExpired =
    isSubscriptionIncompleteExpired(currentCommunity?.subscription?.status);

  const isCommunitySubscriptionIncomplete = isSubscriptionIncomplete(
    currentCommunity?.subscription?.status,
  );

  const isCommunitySubscriptionFullyCancelled = Boolean(
    currentCommunity?.subscription?.is_cancelled &&
      !currentCommunity?.subscription?.on_grace_period,
  );

  const isCommunitySubscriptionOnGracePeriod =
    !!currentCommunity?.subscription?.on_grace_period;

  // past_due or incomplete
  const hasCommunityIncompletePayment =
    !!currentCommunity?.subscription?.has_incomplete_payment;

  const changePlanModalParams = (minimumRequiredTier: PlanTiers) => {
    // Note: for later, we should allow upgrades between the same tier, as some legacy plans
    // doesn't have access to new features already available in the new version of tiers
    const isUpgradingPlan =
      currentPlanTier && isUpgrading(currentPlanTier, minimumRequiredTier);

    if (isCommunityOnTrial) {
      if (currentPlan?.public) {
        return isUpgradingPlan
          ? { planTier: minimumRequiredTier }
          : { planTier: currentPlanTier };
      }

      return isUpgradingPlan
        ? { planTier: minimumRequiredTier }
        : { planId: currentPlan?.id };
    }

    return { planTier: minimumRequiredTier };
  };

  const isCommunityOnPaidPlan = Boolean(
    !isCommunityOnTrial &&
      !isExpiredTrial &&
      currentCommunity?.subscription &&
      !isCommunitySubscriptionIncompleteExpired &&
      !isCommunitySubscriptionFullyCancelled,
  );

  const isDoubleStorageEnabled =
    currentCommunitySettings?.growth_double_storage_pro_biz_enabled;
  const isDoubleStorageApplicable = Boolean(
    isDoubleStorageEnabled &&
      Boolean(isCommunityOnProfessional || isCommunityOnBusiness),
  );

  const shouldShowProration = (newPlan: any) => {
    if (isEmailHubStatusEnabled || isAdminApiActive || isHeadlessApiActive) {
      return false;
    }

    if (!newPlan) {
      return false;
    }

    if (isCommunityOnTrial || isExpiredTrial) {
      return false;
    }

    if (currentPlanInterval != newPlan.interval) {
      return true;
    }

    if (isAnnual(currentPlanInterval) && isAnnual(newPlan.interval)) {
      return true;
    }

    return hasAddons;
  };

  return {
    isCommunityOnTrial,
    isCommunityOnEnterprise,
    isCommunityOnProfessional,
    isCommunityOnBasic,
    isCommunityOnPlus,
    isCommunityOnBusiness,
    isCommunityOnPaidPlan,
    currentPlanTier,
    currentPlanInterval,
    currentPeriodEnd,
    currentPeriodStart,
    hasActiveOffer,
    defaultCurrency,
    canChooseCurrency,
    isExpiredTrial,
    shouldShowProration,
    isCommunitySubscriptionIncompleteExpired,
    isCommunitySubscriptionIncomplete,
    isCommunitySubscriptionFullyCancelled,
    isCommunitySubscriptionOnGracePeriod,
    isDoubleStorageApplicable,
    hasCommunityIncompletePayment,
    isPromoCodeEnabledForCommunity,
    changePlanModalParams,
    currentPlan,
  };
};
